/**
 * Pure price formatter for the pro-profile services catalogue (ticket #5598).
 *
 * A price is two axes: `priceType` (how the amount reads: fixed / from / range) and
 * `priceUnit` (what it is charged per: job / sqm / item / hour). This util composes the
 * two — the type produces the base amount string, the unit appends a `/ suffix` (except
 * `job`, which is a flat price with no suffix). The amount runs through the country
 * currency formatter, so sign placement and grouping follow the locale (Greek reads
 * `1.250 €`, English `€1,250`); the `από` prefix and unit suffixes come from i18n. The
 * `+ VAT` subline is the caller's concern (see `ServicePrice.tsx`) — this util only
 * renders the amount.
 *
 * {@link formatPriceParts} keeps the three pieces apart so the row can weight them
 * separately (only the amount is bold — #5685); {@link formatPrice} joins them back for
 * callers that want the plain string.
 *
 * Returns `undefined` for any price the row cannot render (unknown type/unit, missing
 * amount, range without an upper bound) so the caller can skip it.
 */

import { TFunction } from 'i18next';
import { Formatter } from 'Douleutaras/hooks/localization';

import { PriceType, PriceUnit, ServicePrice } from './types';

export interface PriceFormatDeps {
  formatter: Pick<Formatter, 'number' | 'price'>;
  t: TFunction;
}

/**
 * A price split into the pieces the row weights differently: `από` and the unit stay
 * regular and muted, `amount` carries the emphasis.
 */
export interface FormattedPriceParts {
  /** Localized `από` ("from"). Absent for fixed and range prices. */
  prefix?: string;
  /** The money itself, e.g. `1.250 €`. Always present. */
  amount: string;
  /** Localized `/ τ.μ.` and friends. Absent for a flat `job` price. */
  unitSuffix?: string;
}

const isRenderableAmount = (value: number | undefined): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

/** Every price type this util can render — exhaustive over `PriceType`, so a new one won't slip by. */
const RENDERABLE_PRICE_TYPES: Record<PriceType, true> = { fixed: true, from: true, range: true };

/** Every unit this util can render — exhaustive over `PriceUnit`, so a new one won't slip by. */
const RENDERABLE_PRICE_UNITS: Record<PriceUnit, true> = { job: true, sqm: true, item: true, hour: true };

/**
 * Whether a price renders at all, i.e., whether the row shows an amount rather than label-only.
 * {@link formatPriceParts} guards on this, so tracking (which reports the displayed amount) and
 * the UI cannot drift apart.
 */
export const isDisplayablePrice = (price?: ServicePrice): price is ServicePrice => {
  if (!price || !isRenderableAmount(price.amount) || !RENDERABLE_PRICE_TYPES[price.priceType]) {
    return false;
  }
  if (price.priceType === 'range' && !isRenderableAmount(price.amountMax)) {
    return false;
  }

  // `job` is flat and needs no suffix; every other unit needs one to render.
  return RENDERABLE_PRICE_UNITS[price.priceUnit] === true;
};

/**
 * Whether the locale puts the euro sign after the amount (Greek `250 €`) rather than
 * before it (English `€250`). Drives which are bound of a range carry the sign.
 */
const hasTrailingCurrencySign = (formatter: PriceFormatDeps['formatter']): boolean =>
  /^\d/.test(formatter.price(0));

/** The base amount string for a price type, or `undefined` when it cannot be rendered. */
const formatAmount = (price: ServicePrice, { formatter }: PriceFormatDeps): string | undefined => {
  switch (price.priceType) {
    case 'fixed':
    case 'from':
      return formatter.price(price.amount);

    case 'range':
      if (!isRenderableAmount(price.amountMax)) {
        return undefined;
      }

      // One-euro sign for the pair, on whichever bound the locale attaches it to:
      // `800-3.000 €` in Greek, `€800-3,000` in English.
      return hasTrailingCurrencySign(formatter)
        ? `${formatter.number(price.amount)}-${formatter.price(price.amountMax)}`
        : `${formatter.price(price.amount)}-${formatter.number(price.amountMax)}`;

    default:
      // Unknown price type from BE → skip the row.
      return undefined;
  }
};

/**
 * The `/ suffix` copy for a per-unit price, or `undefined` for a unit we cannot render.
 * `job` is a flat price and never reaches here — see `formatPriceParts`.
 */
const formatUnitSuffix = (priceUnit: PriceUnit, t: TFunction): string | undefined => {
  switch (priceUnit) {
    case 'sqm':
      return t('T.PROS-PROFILE.SERVICES.UNIT-AREA');

    case 'item':
      return t('T.PROS-PROFILE.SERVICES.UNIT-ITEM');

    case 'hour':
      return t('T.PROS-PROFILE.SERVICES.UNIT-HOUR');

    default:
      // Unknown unit from BE → skip the row.
      return undefined;
  }
};

/**
 * Splits a service price into its separately weighted pieces, or `undefined` when it
 * cannot be rendered.
 *
 * | type × unit    | Rendered (Greek)      |
 * |----------------|-----------------------|
 * | fixed  · job   | `150 €`               |
 * | from   · job   | `από` `150 €`         |
 * | range  · job   | `800-3.000 €`         |
 * | fixed  · sqm   | `35 €` `/ τ.μ.`       |
 * | fixed  · item  | `40 €` `/ τεμ.`       |
 * | fixed  · hour  | `30 €` `/ ώρα`        |
 */
export const formatPriceParts = (
  price: ServicePrice | undefined,
  deps: PriceFormatDeps
): FormattedPriceParts | undefined => {
  if (!isDisplayablePrice(price)) {
    return undefined;
  }

  const amount = formatAmount(price, deps);
  if (!amount) {
    return undefined;
  }

  const prefix = price.priceType === 'from'
    ? deps.t('T.PROS-PROFILE.SERVICES.PRICE-FROM')
    : undefined;

  // `job` is a flat price — no per-unit suffix.
  if (price.priceUnit === 'job') {
    return { prefix, amount };
  }

  const unit = formatUnitSuffix(price.priceUnit, deps.t);
  if (!unit) {
    return undefined;
  }

  return { prefix, amount, unitSuffix: `/ ${unit}` };
};

/**
 * The same price as one plain string for callers that cannot use the parts (tracking,
 * accessible labels, tests). Mirrors {@link formatPriceParts} exactly.
 */
export const formatPrice = (
  price: ServicePrice | undefined,
  deps: PriceFormatDeps
): string | undefined => {
  const parts = formatPriceParts(price, deps);

  return parts && [ parts.prefix, parts.amount, parts.unitSuffix ]
    .filter(Boolean)
    .join(' ');
};
